import { getProviderByUserEmail } from '@alucio/aws-beacon-amplify/src/graphql/queries';
import { generateClient } from 'aws-amplify/api';
import { useState, useCallback, useEffect } from 'react';
import useRetryOnErrosSSO from './useRetryOnSSoError';
import useFeatureFlags from 'src/hooks/useFeatureFlags/useFeatureFlags';
import * as logger from 'src/utils/logger';
import { GetProviderByUserEmailQuery } from '@alucio/aws-beacon-amplify/src/API';
import { signInWithRedirect } from '@aws-amplify/auth';
import { useAppSettings } from 'src/state/context/AppSettings';
const navigation = window.location;

const useSSO = () => {
  const [isValidating, setIsValidating] = useState<boolean>(false);
  const [isSSoUser, setIsSSoUser] = useState<boolean>(false);
  const { saveUrl, error: ssoError, cleanSSoError } = useRetryOnErrosSSO();
  const enableZendeskKnowledgebase = useFeatureFlags('enableZendeskKnowledgebase');
  const { initialUrlSearchParams } = useAppSettings()
  useEffect(() => {
    // Handles scenario of user using cognito SSO login link to initiate a
    // SP-Initiated flow. In this case cognito redirects back /wo PKCE
    // We re-initiate signIn back to cognito which redirects back using existing
    // cognito session
    const codeParam = initialUrlSearchParams.get('code') ?? ''
    const stateParam = initialUrlSearchParams.get('state') ?? ''
    if (codeParam && !stateParam) {
      logger.auth.signIn.sso.info('Detected SSO Sign in response without state retrying login via redirect');
      signInWithRedirect({
        provider: {
          // We set this to an empty string which together with patch
          // makes it so we don't request a specific provider
          custom: '',
        },
      });
    }
  }, [navigation.search])

  const validateEmail = (email) => {
    // eslint-disable-next-line max-len
    const re = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    return re.test(String(email).toLowerCase());
  }
  const getProviderForEmail = async (email): Promise<string | null> => {
    setIsValidating(true);
    if (!validateEmail(email)) {
      return null
    }
    const domain: string = email.split('@').pop().toLowerCase();
    const appsyncClient = generateClient<GetProviderByUserEmailQuery>();
    const { data } = await appsyncClient.graphql({
      query: getProviderByUserEmail,
      variables: {
        idpIdentifier: domain,
      },
      authMode: 'iam',
    });
    setIsValidating(false)
    return data.getProviderByUserEmail ?? null;
  }

  const checkSSOAndSignInWithRedirect = async (email: string): Promise<boolean> => {
    logger.auth.signIn.sso.debug('Validating user email', email);

    logger.auth.signIn.sso.info('Started SSO validation');

    try {
      // Since we aren't logged in we use IAM un-authenticated credentials to call appsync
      const result = await getProviderForEmail(email)
      if (result) {
        // The react router is not available at this moment in the app for that reason we use the window.location
        logger.auth.signIn.sso.debug('SSO provider found for email', email);
        const state = `
          ${window.location.pathname}
          ${window.location.search}
          ${window.location.hash}`
          .replace(/\s/g, '')
        saveUrl(result);
        setIsSSoUser(true);
        // This causes user to get redirected
        await signInWithRedirect({
          customState: state,
          provider: {
            custom: result,
          },
        });
        return true;
      } else {
        logger.auth.signIn.sso.debug('No SSO provider found for email', email);
      }
    } catch (error) {
      logger.auth.signIn.sso.error('Error during SSO validation', error);
    }
    setIsSSoUser(false);
    return false;
  };
  const CheckSSOAndSignInWithRedirect = useCallback(checkSSOAndSignInWithRedirect, [enableZendeskKnowledgebase])

  return {
    CheckSSOAndSignInWithRedirect,
    isValidating,
    isSSoUser,
    ssoError,
    cleanSSoError,
    getProviderForEmail,
  }
}

export default useSSO;
